import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, Heart } from 'lucide-react';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay
} from 'date-fns';
import { ko } from 'date-fns/locale';
import { useAppContext } from '../../context/AppContext';
import { TimelineEntry } from '../../types';
import TimelineModal from '../../components/common/TimelineModal';

const CalendarScreen: React.FC = () => {
  const { entries, addEntry, updateEntry } = useAppContext();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimelineEntry | null>(null);

  const daysOfWeek = ['일', '월', '화', '수', '목', '금', '토'];

  const entriesByDate = useMemo(() => {
    const map: Record<string, TimelineEntry[]> = {};
    entries.forEach(entry => {
      if (!map[entry.event_date]) {
        map[entry.event_date] = [];
      }
      map[entry.event_date].push(entry);
    });
    return map;
  }, [entries]);


  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    return eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
  }, [currentMonth]);

  const handleDateClick = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const events = entriesByDate[dateStr] || [];
    
    setSelectedDate(day);
    if (events.length > 0) {
      setEditingEntry(events[0]);
    } else {
      setEditingEntry(null);
    }
    setIsModalOpen(true);
  };

  const handleEventClick = (e: React.MouseEvent, entry: TimelineEntry) => {
    e.stopPropagation();
    setSelectedDate(new Date(entry.event_date));
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const handleSave = async (entryData: Omit<TimelineEntry, 'id' | 'meetingCount'>) => {
    if (editingEntry) {
      await updateEntry(editingEntry.id, entryData);
    } else {
      await addEntry(entryData);
    }
  };

  return (
    <div className="flex flex-col gap-6 pb-24 relative">
      <header className="flex justify-between items-center py-4">
        <h1 className="text-2xl font-bold text-charcoal">우리의 달력</h1>
      </header>

      {/* Floating Action Button */}
      <button 
        onClick={() => {
          setSelectedDate(new Date());
          setEditingEntry(null);
          setIsModalOpen(true);
        }}
        className="fixed bottom-24 right-6 bg-primary text-white p-4 rounded-full shadow-2xl active:scale-95 transition-all z-40"
      >
        <Plus size={24} strokeWidth={3} />
      </button>

      {/* Month Selector */}
      <div className="flex items-center justify-between px-2">
        <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 text-gray-400 hover:text-primary transition-colors cursor-pointer">
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-xl font-black">
          {format(currentMonth, 'yyyy년 M월', { locale: ko })}
        </h2>
        <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 text-gray-400 hover:text-primary transition-colors cursor-pointer">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="card bg-white p-4 shadow-sm overflow-hidden">
        <div className="grid grid-cols-7 gap-y-1 text-center mb-4">
          {daysOfWeek.map((day, i) => (
            <span key={day} className={`text-[10px] font-bold pb-2 ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-300'}`}>
              {day}
            </span>
          ))}
          
          {calendarDays.map((day, idx) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const events = entriesByDate[dateStr] || [];
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = isSameMonth(day, currentMonth);

            return (
              <button 
                key={idx} 
                onClick={() => handleDateClick(day)}
                className={`relative flex flex-col items-center min-h-[80px] p-1 border-t border-gray-50 hover:bg-primary/5 transition-colors cursor-pointer
                  ${!isCurrentMonth ? 'opacity-20' : ''}
                `}
              >
                <span className={`text-[11px] font-bold w-6 h-6 flex items-center justify-center rounded-full transition-all mb-1
                  ${isToday ? 'bg-primary text-white shadow-md' : 'text-charcoal'}
                `}>
                  {format(day, 'd')}
                </span>
                
                <div className="flex flex-col gap-0.5 w-full overflow-y-auto max-h-[50px] scrollbar-hide">
                  {events.slice(0, 3).map((event) => (
                    <div 
                      key={event.id} 
                      onClick={(e) => handleEventClick(e, event)}
                      className="text-[7px] bg-primary/10 text-primary px-1 py-0.5 rounded truncate leading-tight border border-primary/10 font-bold hover:bg-primary hover:text-white transition-colors"
                    >
                      {event.title}
                    </div>
                  ))}
                  {events.length > 3 && (
                    <div className="text-[7px] text-gray-400 font-bold mt-0.5">
                      +{events.length - 3}개 더...
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Today's Schedule */}
      <section>
        <h3 className="text-xs font-bold text-gray-400 mb-4 px-1 uppercase tracking-wider text-left">기념일 가이드</h3>
        <div className="card flex items-center gap-4 border-l-4 border-l-primary bg-white">
          <div className="flex-1 text-left">
            <p className="font-bold text-sm">추억을 기록하고 관리하세요</p>
            <p className="text-[10px] text-gray-400">날짜를 클릭하여 새로운 추억을 추가하거나 기존 기록을 수정할 수 있습니다.</p>
          </div>
          <Heart size={16} className="text-primary fill-primary opacity-20" />
        </div>
      </section>

      <TimelineModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        editingEntry={editingEntry}
        defaultDate={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined}
      />
    </div>
  );
};

export default CalendarScreen;
